<template>
  <svg v-if="isMarker" viewBox="-2 -2 81 81" class="token-svg" aria-hidden="true">
    <path d="M 0, 38 a 38,38 0 1,1 76,0 a 38,38 0 1,1 -76,0" fill="#7e7e7e" style="pointer-events: fill" />
    <defs>
      <clipPath :id="clipId" clipPathUnits="userSpaceOnUse">
        <circle cx="38.5" cy="38.5" r="38.5" />
      </clipPath>
    </defs>
    <svg :clip-path="`url(#${clipId})`" width="77" height="77">
      <image
        v-if="hasRect && !useFallback"
        :href="resolvedPath"
        :transform="transformValue"
        @error="onImageError"
      />
      <image
        v-else
        :href="resolvedPath"
        width="77"
        height="77"
        @error="onImageError"
      />
    </svg>
    <path
      d="M 0, 38 a 38,38 0 1,1 76,0 a 38,38 0 1,1 -76,0"
      fill="none"
      stroke="#BBBBBB"
      stroke-width="2"
    />
  </svg>

  <svg v-else viewBox="-2 -2 197 172" class="token-svg" aria-hidden="true">
    <defs>
      <clipPath :id="clipId" clipPathUnits="userSpaceOnUse">
        <polygon points="48 0, 0 83.5, 48 167, 144 167, 192 83.5, 144 0" />
      </clipPath>
    </defs>
    <path d="M 48,1 L 0,85 48,167 144,167 192,85 144,1 48,1 Z" fill="#7e7e7e" style="pointer-events: fill" />
    <svg :clip-path="`url(#${clipId})`" width="192" height="167">
      <image
        v-if="hasRect && !useFallback"
        :href="resolvedPath"
        :transform="transformValue"
        @error="onImageError"
      />
      <image
        v-else
        :href="resolvedPath"
        width="192"
        height="167"
        @error="onImageError"
      />
    </svg>
    <path
      d="M 48,1 L 0,85 48,167 144,167 192,85 144,1 48,1 Z"
      fill="none"
      stroke="#BBBBBB"
      stroke-width="2"
    />
  </svg>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'

interface TokenRect {
  x?: number
  y?: number
  w?: number
  h?: number
}

const props = defineProps<{
  path?: string
  kind: 'h' | 'b' | 'a' | 'u' | 'f' | 'l' | 'm'
  rect?: TokenRect | null
}>()

const FALLBACK_IMAGE = '/media/site/errortoken.avif'
const clipId = `token-clip-${Math.random().toString(36).slice(2, 10)}`

const useFallback = ref(false)
watch(() => props.path, () => {
  useFallback.value = false
})

const isMarker = computed(() => props.kind === 'l' || props.kind === 'm')

function toNumber(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return value
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value)
    return Number.isFinite(parsed) ? parsed : null
  }
  return null
}

const rectValues = computed(() => {
  const x = toNumber(props.rect?.x)
  const y = toNumber(props.rect?.y)
  const w = toNumber(props.rect?.w)
  const h = toNumber(props.rect?.h)
  return { x, y, w, h }
})

const hasRect = computed(() => {
  const { x, y, w, h } = rectValues.value
  return x !== null && y !== null && w !== null && h !== null && w > 0 && h > 0
})

const transformValue = computed(() => {
  if (!hasRect.value) return undefined
  const { x, y, w, h } = rectValues.value
  const targetW = isMarker.value ? 77 : 192
  const targetH = isMarker.value ? 77 : 167
  const scaleX = targetW / (w as number)
  const scaleY = targetH / (h as number)
  return `scale(${scaleX}, ${scaleY}) translate(-${x}, -${y})`
})

const resolvedPath = computed(() => {
  if (useFallback.value) return FALLBACK_IMAGE
  return props.path || FALLBACK_IMAGE
})

function onImageError() {
  useFallback.value = true
}
</script>

<style scoped>
.token-svg {
  width: 3.5rem;
  display: inline-block;
  vertical-align: middle;
}
</style>
